/// <reference path="../../types/global.d.ts" />
import { useEffect, useRef, useState } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { CONFIG } from '../../common/constants';
import PropTypes from 'prop-types';

const getLocalStorageItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return null;
  }
};

export function ABTestingProvider({ children }) {
  const gbRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  // Initialize GrowthBook instance once
  if (!gbRef.current && CONFIG.GROWTH_BOOK_API_HOST && CONFIG.GROWTH_BOOK_CLIENT_KEY) {
    gbRef.current = new GrowthBook({
      attributes: { role: 'company' },
      apiHost: CONFIG.GROWTH_BOOK_API_HOST,
      clientKey: CONFIG.GROWTH_BOOK_CLIENT_KEY,
      enableDevMode: CONFIG.ENV_NAME === 'qualrow',
      backgroundSync: false,
      subscribeToChanges: false,
    });
  }

  // Initial initialization
  useEffect(() => {
    async function initializeGrowthBook() {
      if (!gbRef.current) {
        setIsInitialized(true);
        return;
      }

      try {
        await gbRef.current.init();
        if (typeof window !== 'undefined') {
          window.growthbook = gbRef.current;
        }
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize GrowthBook:', error);
        setIsInitialized(true);
      }
    }

    void initializeGrowthBook();
  }, []);

  useEffect(() => {
    const checkCompanyDetails = () => {
      const details = getLocalStorageItem('wse.companyDetails');
      if (details) {
        setCompanyDetails(details);
      }
    };

    checkCompanyDetails();

    const handleCompanyDetailsLoaded = (event) => {
      if (event.detail) {
        setCompanyDetails(event.detail);
      }
    };

    window.addEventListener('companyDetailsLoaded', handleCompanyDetailsLoaded);

    return () => {
      window.removeEventListener('companyDetailsLoaded', handleCompanyDetailsLoaded);
    };
  }, []);

  useEffect(() => {
    if (companyDetails && gbRef.current && isInitialized) {
      try {
        gbRef.current.setAttributes({
          role: 'company',
          companyId: companyDetails.companyId,
          companyReferenceId: companyDetails.companyReferenceId,
          territoryId: companyDetails.territoryId,
          territoryName: companyDetails.territoryName,
          centerId: companyDetails.centerId,
          centerReferenceId: companyDetails.centerReferenceId,
          centerName: companyDetails.centerName,
          isPhoneFieldEnabled: companyDetails.isPhoneFieldEnabled || false,
          isRightToLeftLanguageLayout: companyDetails.isRightToLeftLanguageLayout || false,
          showResults: companyDetails.showResults || false,
          registrationType: companyDetails.registrationType,
          companyType: companyDetails.companyType,
          countryCode: companyDetails.countryCode,
          languageCode: companyDetails.languageCode || 'en',
        });
        console.log('GrowthBook attributes updated with company details');
      } catch (error) {
        console.error('Failed to set GrowthBook attributes:', error);
      }
    }
  }, [companyDetails, isInitialized]);

  if (!isInitialized) {
    return null;
  }

  if (!gbRef.current) {
    gbRef.current = new GrowthBook({
      attributes: {},
    });
  }

  return (
    <GrowthBookProvider growthbook={gbRef.current}>
      {children}
    </GrowthBookProvider>
  );
}

ABTestingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
